import { z } from 'zod';
import {
  birthdateSchema,
  emailSchema,
  firstNameSchema,
  identificationSchema,
  lastNameSchema,
  phoneSchema
} from './common/schemas';
import { differenceInYears } from 'date-fns';

const illnessSchema = z.discriminatedUnion(
  'value',
  [
    z.object({
      value: z.literal<boolean>(true, {
        invalid_type_error: 'schemas:common.fieldObligatory'
      }),
      answer: z
        .string({ required_error: 'schemas:common.fieldObligatory' })
        .min(1, 'schemas:common.fieldRequired')
    }),
    z.object({
      value: z.literal<boolean>(false, {
        invalid_type_error: 'schemas:common.fieldObligatory'
      }),
      answer: z
        .string()
        .optional()
        .transform(() => '')
    })
  ],
  {
    errorMap: (issue, ctx) => ({
      message:
        issue.code === 'invalid_union_discriminator'
          ? `schemas:common.fieldObligatory`
          : ctx.defaultError
    })
  }
);

const userParticipantSchema = (maxAge?: number) => {
  return z.object({
    identification: identificationSchema,
    email: emailSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    phone: phoneSchema,
    birthdate: birthdateSchema({ minAge: 18, maxAge }),
    address: z
      .string()
      .min(2, {
        message: 'schemas:common.addressRequired'
      })
      .min(1),
    location: z.string().min(2, {
      message: 'schemas:common.cityRequired'
    }),
    postalCode: z.string().min(2, {
      message: 'schemas:common.zipCodeRequired'
    }),
    illness: illnessSchema
  });
};

const userSchema = z.object({
  identification: identificationSchema,
  email: emailSchema,
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  phone: phoneSchema,
  birthdate: z.date().optional(),
  address: z.string().optional(),
  location: z.string().optional(),
  postalCode: z.string().optional()
});

const participantSchema = ({
  minAge,
  maxAge
}: {
  minAge?: number;
  maxAge?: number;
}) => {
  return z.object({
    id: z.string().optional(),
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    identification: identificationSchema,
    birthdate: birthdateSchema({ minAge, maxAge }),
    address: z.string().min(2, {
      message: 'schemas:common.addressRequired'
    }),
    location: z.string().min(2, {
      message: 'schemas:common.cityRequired'
    }),
    postalCode: z.string().min(2, {
      message: 'schemas:common.zipCodeRequired'
    }),
    illness: illnessSchema
  });
};

const participantOptSchema = z.object({
  id: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  identification: z.object({
    number: z.string().optional(),
    type: z.string().optional()
  }),
  birthdate: z.date().optional(),
  address: z.string().optional(),
  location: z.string().optional(),
  postalCode: z.string().optional(),
  illness: illnessSchema.optional()
});

const formParticipantDataWingfoilSchema = ({
  minAge,
  maxAge
}: {
  minAge?: number;
  maxAge?: number;
}) => {
  return z
    .discriminatedUnion('userIsParticipant', [
      z.object({
        userIsParticipant: z.literal<boolean>(true),
        serviceDivisionGroups: z.array(z.string()).min(1).max(1),
        user: userParticipantSchema(maxAge),
        participant: participantOptSchema,
        emergencyContact: z.object({
          fullName: z.string().min(2, {
            message: 'schemas:common.firstNameRequired'
          }),
          phone: phoneSchema
        }),
        acceptDataPrivacy: z.boolean().refine((val) => val === true, {
          message: 'schemas:common.dataPrivacyRequired'
        }),
        acceptTakePhotos: z.boolean().optional(),
        authOfferProducts: z.boolean().optional()
      }),
      z.object({
        userIsParticipant: z.literal<boolean>(false),
        serviceDivisionGroups: z.array(z.string()).min(1).max(1),
        user: userSchema,
        participant: participantSchema({ minAge, maxAge }),
        emergencyContact: z.object({
          fullName: z.string().min(2, {
            message: 'schemas:common.firstNameRequired'
          }),
          phone: phoneSchema
        }),
        acceptDataPrivacy: z.boolean().refine((val) => val === true, {
          message: 'schemas:common.dataPrivacyRequired'
        }),
        acceptTakePhotos: z.boolean().optional(),
        authOfferProducts: z.boolean().optional()
      })
    ])
    .superRefine((data, ctx) => {
      if (data.userIsParticipant) {
        const birthdate = data.user?.birthdate;
        if (!birthdate) return;

        const age = differenceInYears(new Date(), new Date(birthdate));

        if (age < 18) {
          ctx.addIssue({
            path: ['userIsParticipant'],
            code: z.ZodIssueCode.custom,
            message: 'schemas:common.participantMinorCheckbox'
          });
        }
      }
    });
};

export default formParticipantDataWingfoilSchema;
